// import { getIamportPayload, IamportPayload, PayMethod, requestPay, useIamport } from '@/lib/iamport'
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { SHOP_CHECKOUT_COMPLETE_PAGE } from "@/settings/constant";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { postOrder } from "src/api/booksOrder";
import { postPaymentConfirm, postZeroPayment } from "src/api/payment";
import { shallow } from "zustand/shallow";
import { patchUserCoupon } from "../../../api/coupon";
import useCheckoutStore from "../../../store/checkout.store";
import usePgBridge from "../pg/usePgBridge";
import { getMixpanelOrderType } from "../utils/checkoutTracking";
import { createUrlWithParams, URL_PARAMS, validatePaymentConditions } from "../utils/payUtils";
import useAlwaysUseCash from "./useAlwaysUseCash";
import { getOrderTypeFromParams, parseTossFailParams, parseTossSuccessParams, useOrderInfo, usePassAmountType } from "./usePaymentUtils";
const usePayment = (param)=>{
    let { paymentData , paymentLoading , hasCoupon , orderType: orderPropsType , buttonName , id: idProp , variantKey , onError  } = param;
    const router = useRouter();
    const { id: idQuery , orderType: orderTypeQuery , loading: loadingToss  } = router.query;
    const id = idProp !== null && idProp !== void 0 ? idProp : idQuery;
    const orderType = orderPropsType !== null && orderPropsType !== void 0 ? orderPropsType : orderTypeQuery;
    const [payRequestLoading, setPayRequestLoading] = useState(false);
    const queryClient = useQueryClient();
    const { orderItems , initializeStore , priceInfo , deliveryInfo , clearAllCoupons , isAlwaysUseCash  } = useCheckoutStore((state)=>{
        return {
            orderItems: state.orderItems,
            initializeStore: state.initializeStore,
            priceInfo: state.priceInfo,
            deliveryInfo: state.deliveryInfo,
            clearAllCoupons: state.clearAllCoupons,
            isAlwaysUseCash: state.isAlwaysUseCash
        };
    }, shallow);
    // 항상 전액 사용 로컬스토리지 저장
    const { setValue: setAlwaysUseCashValue  } = useAlwaysUseCash();
    // 쿠폰 적용을 위한 patch
    const patchUserCouponMutation = useMutation(patchUserCoupon);
    const postOrderMutation = useMutation(postOrder);
    // 결제 승인을 위한 mutation
    const confirmPaymentMutation = useMutation(postPaymentConfirm);
    // 0원 결제 처리를 위한 mutation
    const postZeroPaymentMutation = useMutation(postZeroPayment);
    // orderInfo 추출
    const orderInfo = useOrderInfo(paymentData);
    const isPassAmountType = usePassAmountType(paymentData, orderType);
    // 토스 pg사 결제 준비 및 결제 요청
    const { ready , mount , assertPayable , requestPayment  } = usePgBridge({
        orderType: orderType,
        amount: priceInfo.finalPrice,
        isPassAmountType,
        packageVariantKey: variantKey
    });
    // 결제 toss 리다이렉트시 로딩 초기화
    const clearPayLoading = ()=>{
        setPayRequestLoading(false);
    };
    // 이용권과 패키지, 전자책, 캐시상품의 경우 merchantUid 생성
    const createOrderId = async (category)=>{
        if (orderType === "separate" || orderType === "delivery" || orderType === "pod" || orderType === "upgrade") return {
            merchantUid: paymentData === null || paymentData === void 0 ? void 0 : paymentData.merchantUid,
            id: id.toString()
        };
        try {
            const order = await postOrderMutation.mutateAsync(orderType === "ebook" ? {
                orderType: getOrderTypeFromParams(orderType, category),
                cartId: id.toString()
            } : {
                orderType: getOrderTypeFromParams(orderType, category),
                productId: Number(id)
            });
            return {
                merchantUid: order.merchantUid,
                id: order.id.toString()
            };
        } catch (error) {
            ;
            onError === null || onError === void 0 ? void 0 : onError({
                message: "주문 생성에 실패하였습니다.",
                type: "order_creation_error"
            });
            return null;
        }
    };
    // 주문서 생성 후, toss payments 결제 요청
    const onPay = async (merchantUid)=>{
        // 조건 체크를 먼저 수행
        if (!validatePaymentConditions({
            info: orderInfo,
            total: priceInfo.finalPrice
        })) {
            return;
        }
        try {
            // 0원 결제 일때 be api 추가
            await requestPayment({
                orderId: merchantUid,
                orderName: orderInfo.orderName,
                successUrl: createUrlWithParams(URL_PARAMS.SUCCESS),
                failUrl: createUrlWithParams(URL_PARAMS.FAIL),
                customerEmail: orderInfo.customerEmail,
                customerName: orderInfo.customerName,
                customerMobilePhone: orderInfo.customerMobilePhone || null
            });
        } catch (error) {
            // 에러 처리하기
            if (error.code === "INVALID_PARAMETERS") {
                // 전화번호가 없는 고객님 임시 처리 25.07.31
                onError === null || onError === void 0 ? void 0 : onError({
                    message: "결제를 진행할 수 없습니다. 고객센터로 문의해주세요.",
                    type: "payment_error"
                });
                return;
            }
            alert(error.message);
        }
    };
    const handleZeroPayment = async (merchantUid)=>{
        // 0원 결제 일때 be api 추가
        try {
            const result = await postZeroPaymentMutation.mutateAsync({
                orderId: merchantUid
            });
            if (result.orderId) {
                handlePaymentSuccess({
                    orderId: result.orderId
                });
            }
        } catch (error) {
            clearPayLoading();
            onError === null || onError === void 0 ? void 0 : onError({
                message: "0원 결제에 실패하였습니다.",
                type: "payment_error"
            });
        }
    };
    // 결제 처리 함수
    const paymentHandle = async (category)=>{
        if (!orderInfo) {
            onError === null || onError === void 0 ? void 0 : onError({
                message: "주문 정보를 불러올 수 없어요. 새로고침 후 다시 시도해주세요.",
                type: "order_creation_error"
            });
            return;
        }
        // mx-316 / MX-3
        // 위젯 결제 경로.
        // 'Is Terms Agreed' 는 이 경로에 약관 동의 UI 자체가 없어서, 'Payment Method' 는 결제수단을
        // 토스 위젯이 소유해 클릭 시점에 알 수 없어서 보내지 않는다.
        // (월구독은 CheckoutView.handleSubmitPayment 에서 그 값들까지 담아 따로 발사한다)
        mixpanelEvent("Purchase Button Clicked", {
            "Product Categories": orderType,
            "Product ID": id,
            "Product Title": orderInfo.orderName,
            "Product Price": priceInfo.finalPrice,
            "Order Type": getMixpanelOrderType(orderType),
            ...buttonName && {
                "Button Name": buttonName
            },
            // 위젯 결제에는 무료체험 개념이 없다
            "Is Free Trial": false
        });
        // 위젯 결제인 경우, 주문을 만들기 전에 결제 가능 여부를 확인한다.
        // 주문 생성과 쿠폰·캐시·포인트 patch가 먼저 일어나므로, 여기서 막지 않으면
        // 서버 자원을 소모한 뒤 결제창을 못 띄우는 상태가 된다.
        if (priceInfo.finalPrice > 0) {
            try {
                assertPayable();
            } catch (error) {
                onError === null || onError === void 0 ? void 0 : onError({
                    message: error instanceof Error ? error.message : "결제를 진행할 수 없습니다.",
                    type: "payment_error"
                });
                return;
            }
        }
        // 1. pass, package, ebook의 경우, merchantUid 생성 필요
        const orderResult = await createOrderId(category);
        if (!orderResult) {
            onError === null || onError === void 0 ? void 0 : onError({
                message: "주문 생성에 실패하였습니다.",
                type: "order_creation_error"
            });
            return;
        }
        const { id: orderId , merchantUid  } = orderResult;
        // 0원 결제의 경우에만 로딩 상태 설정 (토스 결제는 리다이렉트 후 useEffect에서 처리)
        if (priceInfo.finalPrice === 0) {
            setPayRequestLoading(true);
        }
        // 2. 해당 api는 쿠폰, 캐시, 포인트 결제를 위해 기본적으로 호출해야 하는 함수입니다.
        patchUserCouponMutation.mutate({
            orderId: orderId || id.toString(),
            total: priceInfo.finalPriceWithCoupon,
            items: hasCoupon ? orderItems.filter((item)=>item.isCouponApplied).map((item)=>({
                    orderItemId: item.id,
                    couponId: item.appliedCouponId
                })) : [],
            ...orderType === "delivery" || orderType === "pod" ? (deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.deliveryFeeCouponId) ? {
                deliveryFeeCouponId: deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.deliveryFeeCouponId
            } : {} : {},
            cash: priceInfo.usedCash,
            point: priceInfo.usedPoint,
            pgAmount: priceInfo.finalPrice
        }, {
            onSuccess: ()=>{
                // 3. 결제 요청
                if (priceInfo.finalPrice === 0) {
                    handleZeroPayment(merchantUid);
                } else {
                    onPay(merchantUid);
                }
            },
            onError: async (e)=>{
                if (priceInfo.finalPrice === 0) {
                    clearPayLoading();
                }
                onError === null || onError === void 0 ? void 0 : onError({
                    message: "결제 처리 중 오류가 발생하였습니다.",
                    type: "coupon_error"
                });
                Sentry.captureException(e);
                await queryClient.invalidateQueries([
                    "order",
                    id
                ]);
                // 쿠폰 적용 실패 시 모든 쿠폰 해제
                clearAllCoupons();
            }
        });
    };
    // 결제 성공 처리 함수
    const handlePaymentSuccess = async (params)=>{
        try {
            // 결제 성공 시 '항상 전액 사용' 설정을 로컬스토리지에 저장
            setAlwaysUseCashValue(isAlwaysUseCash !== null && isAlwaysUseCash !== void 0 ? isAlwaysUseCash : false);
            // 토스 결제 성공 후, 서버에 결제 승인 요청
            if ("paymentKey" in params) {
                const result = await confirmPaymentMutation.mutateAsync({
                    paymentKey: params.paymentKey,
                    orderId: params.orderId,
                    amount: params.amount
                });
                if (orderType === "ebook") return;
                return router.replace("".concat(SHOP_CHECKOUT_COMPLETE_PAGE, "?orderId=").concat(result.orderId));
            }
            // 결제 완료 페이지로 리다이렉트
            router.replace("".concat(SHOP_CHECKOUT_COMPLETE_PAGE, "?orderId=").concat(params === null || params === void 0 ? void 0 : params.orderId));
        } catch (error) {
            ;
            onError === null || onError === void 0 ? void 0 : onError({
                message: "결제 승인에 실패하였습니다. 고객센터에 문의해주세요.",
                type: "payment_error"
            });
            clearPayLoading() // 내부상태 정리
            ;
            // 토스에서는 승인이지만, confirm에서 승인 실패 시 URL에서 success 관련 쿼리 파라미터 제거 (id, orderType만 유지)
            const currentQuery = {
                ...router.query
            };
            const cleanQuery = {};
            // id와 orderType만 유지
            if (currentQuery.id) cleanQuery.id = currentQuery.id;
            if (currentQuery.orderType) cleanQuery.orderType = currentQuery.orderType;
            cleanQuery.loading = "false";
            router.replace({
                pathname: router.pathname,
                query: cleanQuery
            });
        }
    };
    // 결제 실패 처리 함수
    const handlePaymentFail = (params)=>{
        // 에러 메시지 표시
        clearPayLoading();
        // 결제 취소/실패 시 쿠폰 상태 초기화 (백엔드와 동기화)
        clearAllCoupons();
        onError === null || onError === void 0 ? void 0 : onError({
            message: "결제에 실패했습니다: ".concat(params.message),
            type: "payment_error"
        });
    };
    // 결제 성공 실패 리다이렉트 처리
    useEffect(()=>{
        // Toss Payments 파라미터 처리
        const successParams = parseTossSuccessParams(router.query);
        const failParams = parseTossFailParams(router.query);
        if (successParams) {
            handlePaymentSuccess(successParams);
        }
        if (failParams) {
            handlePaymentFail(failParams);
        }
    }, [
        router.query
    ]);
    // 결제 데이터 초기화
    useEffect(()=>{
        if (!paymentData) return;
        initializeStore(paymentData, orderType);
    }, [
        paymentData,
        orderType
    ]);
    // router.query로 초기화를 했을 경우, hydration 오류 발생하여 추가
    useEffect(()=>{
        if (loadingToss === "true") {
            setPayRequestLoading(true);
        } else {
            setPayRequestLoading(false);
        }
    }, [
        loadingToss
    ]);
    return {
        ready,
        mount,
        payRequestLoading,
        paymentHandle
    };
};
export default usePayment;
